import React, { useState, useEffect } from 'react';
import { X, Save, Bell, Users, Settings } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';

interface NotificationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RoleNotificationSettings {
  role: string;
  production_notifications: boolean;
  sample_notifications: boolean;
  express_notifications: boolean;
  express_sample_notifications: boolean;
  system_notifications: boolean;
}

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({ isOpen, onClose }) => {
  const [roleSettings, setRoleSettings] = useState<RoleNotificationSettings[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Roles disponibles en el sistema
  const availableRoles = ['Administrador', 'Vendedor', 'Producción', 'Depósito'];

  useEffect(() => {
    if (isOpen) {
      fetchRoleSettings();
    }
  }, [isOpen]);

  const fetchRoleSettings = async () => {
    setIsLoading(true);
    try {
      // Obtener configuraciones existentes por rol
      const { data, error } = await supabase
        .from('role_notification_settings')
        .select('*')
        .order('role');

      if (error) {
        // Si la tabla no existe, crear configuraciones por defecto
        if (error.code === '42P01') { // Table doesn't exist
          await createDefaultRoleSettings();
          return;
        }
        throw error;
      }

      if (data && data.length > 0) {
        setRoleSettings(data);
      } else {
        await createDefaultRoleSettings();
      }
    } catch (error) {
      console.error('Error fetching role settings:', error);
      toast.error('Error al cargar configuraciones');
    } finally {
      setIsLoading(false);
    }
  };

  const createDefaultRoleSettings = async () => {
    try {
      const defaultSettings: Omit<RoleNotificationSettings, 'id'>[] = availableRoles.map(role => ({
        role,
        production_notifications: role === 'Producción',
        sample_notifications: role === 'Producción',
        express_notifications: role === 'Producción',
        express_sample_notifications: role === 'Producción',
        system_notifications: true,
      }));

      const { data, error } = await supabase
        .from('role_notification_settings')
        .insert(defaultSettings)
        .select();

      if (error) throw error;
      setRoleSettings(data || []);
    } catch (error) {
      console.error('Error creating default settings:', error);
      toast.error('Error al crear configuraciones por defecto');
    }
  };

  const handleSettingChange = (roleIndex: number, setting: keyof Omit<RoleNotificationSettings, 'id' | 'role'>, value: boolean) => {
    const newSettings = [...roleSettings];
    newSettings[roleIndex] = {
      ...newSettings[roleIndex],
      [setting]: value,
    };
    setRoleSettings(newSettings);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Actualizar todas las configuraciones
      for (const setting of roleSettings) {
        const { error } = await supabase
          .from('role_notification_settings')
          .upsert(setting, { onConflict: 'role' });

        if (error) throw error;
      }

      // Actualizar las preferencias de todos los usuarios según su rol
      await updateUserPreferences();

      toast.success('Configuraciones guardadas correctamente');
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar configuraciones');
    } finally {
      setIsSaving(false);
    }
  };

  const updateUserPreferences = async () => {
    try {
      // Obtener todos los usuarios usando RPC o consulta directa
      const { data: users, error: usersError } = await supabase
        .rpc('get_users_with_roles');

      if (usersError) {
        // Fallback: usar consulta directa a auth.users
        const { data: authUsers, error: authError } = await supabase
          .from('auth.users')
          .select('id, app_role');
        
        if (authError) {
          console.error('Error getting users:', authError);
          return;
        }
        
        // Actualizar preferencias de cada usuario según su rol
        for (const user of authUsers || []) {
          const roleSetting = roleSettings.find(s => s.role === user.app_role);
          if (roleSetting) {
            const { error } = await supabase
              .from('notification_preferences')
              .upsert({
                user_id: user.id,
                production_notifications: roleSetting.production_notifications,
                sample_notifications: roleSetting.sample_notifications,
                express_notifications: roleSetting.express_notifications,
                express_sample_notifications: roleSetting.express_sample_notifications,
                system_notifications: roleSetting.system_notifications,
              }, { onConflict: 'user_id' });

            if (error) {
              console.error(`Error updating preferences for user ${user.id}:`, error);
            }
          }
        }
      } else {
        // Usar datos de la función RPC
        for (const user of users || []) {
          const roleSetting = roleSettings.find(s => s.role === user.app_role);
          if (roleSetting) {
            const { error } = await supabase
              .from('notification_preferences')
              .upsert({
                user_id: user.id,
                production_notifications: roleSetting.production_notifications,
                sample_notifications: roleSetting.sample_notifications,
                express_notifications: roleSetting.express_notifications,
                express_sample_notifications: roleSetting.express_sample_notifications,
                system_notifications: roleSetting.system_notifications,
              }, { onConflict: 'user_id' });

            if (error) {
              console.error(`Error updating preferences for user ${user.id}:`, error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating user preferences:', error);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Administrador':
        return '👑';
      case 'Vendedor':
        return '💼';
      case 'Producción':
        return '🏭';
      case 'Depósito':
        return '📦';
      default:
        return '👤';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
              <Bell className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Configuración de Notificaciones por Rol
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configura qué notificaciones recibe cada rol de usuario
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Cargando configuraciones...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Settings className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Cómo funciona
                    </h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Configura aquí qué tipos de notificaciones recibe cada rol de usuario. 
                      Los cambios se aplicarán automáticamente a todos los usuarios con ese rol.
                    </p>
                  </div>
                </div>
              </div>

              {/* Role Settings */}
              {roleSettings.map((roleSetting, index) => (
                <div key={roleSetting.role} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-2xl">{getRoleIcon(roleSetting.role)}</span>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {roleSetting.role}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.production_notifications}
                        onChange={(e) => handleSettingChange(index, 'production_notifications', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Producciones
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Notificaciones de nuevas producciones
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.sample_notifications}
                        onChange={(e) => handleSettingChange(index, 'sample_notifications', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Muestras
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Notificaciones de nuevas muestras
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.express_notifications}
                        onChange={(e) => handleSettingChange(index, 'express_notifications', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Express
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Notificaciones de nuevos express
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={roleSetting.express_sample_notifications}
                        onChange={(e) => handleSettingChange(index, 'express_sample_notifications', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Muestras Express
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Notificaciones de nuevas muestras express
                        </p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer md:col-span-2">
                      <input
                        type="checkbox"
                        checked={roleSetting.system_notifications}
                        onChange={(e) => handleSettingChange(index, 'system_notifications', e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Notificaciones del Sistema
                        </span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Notificaciones generales del sistema (mantenimiento, actualizaciones, etc.)
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Guardando...' : 'Guardar Cambios'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsModal;
